import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Paper, Stack, Typography, Alert,
} from '@mui/material';
import api, { setLegalRequiredCallback, resetLegalRequiredHandler } from 'core/services/api';
import { useAuth } from 'modules/auth/contexts/AuthContext';

export const DOC_LABEL = {
    TERMS: 'Terms & Conditions',
    PRIVACY: 'Privacy Policy',
    LOCATION_TRACKING: 'Location & Tracking Notice',
};

// Exact per-document acknowledgement labels (§3/§4). Never preselect any of them.
export const CHECK_TEXT = {
    TERMS: 'I have read and accept the Terms & Conditions',
    PRIVACY: 'I have read and accept the Privacy Policy',
    LOCATION_TRACKING: 'I have read and accept the Location & Tracking Notice',
};

export const GATE_TITLE = 'Legal & Privacy';
export const GATE_SUBTITLE =
    'Please review and accept the following documents before continuing.';
// Shown when the employee already acknowledged older versions (§15).
export const NEW_DOCS_SUBTITLE =
    'New Legal & Privacy documents require your acknowledgement.';
export const ACCEPT_LABEL = 'Accept & Continue';
export const SUCCESS_MESSAGE = 'Legal & Privacy consent recorded successfully.';
export const SUBMIT_ERROR_MESSAGE = 'Unable to record your consent. Please try again.';

const APP_VERSION = process.env.REACT_APP_VERSION || 'web';

export const DocumentBody = ({ doc }) => (
    <Box>
        <Typography variant="caption" color="text.secondary">
            Version {doc.version}{doc.effective_date ? ` · Effective ${doc.effective_date}` : ''}
        </Typography>
        <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, mt: 1 }}>
            {doc.content}
        </Typography>
    </Box>
);

/**
 * Central legal/privacy gate (§4-§6, §17). Wraps the authenticated layout in
 * App.jsx, so it runs for every authenticated route: fresh login, reload /
 * session restore, logout->login and deep links. It never renders for a
 * logged-out user (no checkboxes on the login screen, §3).
 *
 * FAIL-CLOSED: while the policy status is unknown or /legal/status/ fails,
 * the app stays behind the gate with an error + Retry (§17). Only an explicit
 * server response with no pending mandatory document opens the app. The
 * acknowledgement record lives server-side (append-only) and the backend
 * re-checks after every save — local state is never the source of truth.
 */
const LegalGate = ({ children }) => {
    const { logout } = useAuth();
    const [pending, setPending] = useState(null);   // null = checking (fail-closed)
    const [docs, setDocs] = useState([]);           // every published doc + acknowledged flag
    const [checked, setChecked] = useState({});     // {versionId: bool} — never preselected
    const [viewing, setViewing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [accepted, setAccepted] = useState(false);// brief success state
    const [error, setError] = useState('');

    const applyStatus = useCallback((payload) => {
        const pendingList = Array.isArray(payload?.pending) ? payload.pending : [];
        setDocs(Array.isArray(payload?.documents) ? payload.documents : []);
        setPending(pendingList);
        // No checkbox is ever preselected (§7).
        const next = {};
        pendingList.forEach((d) => { next[d.id] = false; });
        setChecked(next);
        if (pendingList.length > 0) setAccepted(false);
    }, []);

    const refresh = useCallback(() => {
        setError('');
        return api.getLegalStatus()
            .then((r) => applyStatus(r.data))
            .catch((e) => {
                // Fail closed (§17): status unknown -> gate + Retry, never a
                // silent allow and never a lock without a way out.
                setPending(null);
                setError(
                    e?.response?.data?.error ||
                    'Unable to verify legal document status. Check your connection and try again.'
                );
            });
    }, [applyStatus]);

    // Cold start / session restore / logout->login / hard refresh.
    useEffect(() => {
        refresh();
    }, [refresh]);

    // Server-side enforcement: a 403 LEGAL_ACKNOWLEDGEMENT_REQUIRED from any
    // business endpoint re-opens the gate mid-session (a new mandatory
    // version was published). Tokens stay valid; only business calls are
    // blocked server-side.
    useEffect(() => {
        setLegalRequiredCallback(refresh);
        return resetLegalRequiredHandler;
    }, [refresh]);

    // Re-check when the tab regains focus/visibility (ack possibly done on
    // another device, or a document published meanwhile).
    useEffect(() => {
        const onFocus = () => { if (pending !== null && pending.length > 0) refresh(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [refresh, pending]);

    const optionalDocs = useMemo(() => docs.filter((d) => !d.mandatory && !d.acknowledged), [docs]);
    const alreadyAcked = useMemo(
        () => docs.filter((d) => d.acknowledged && !(pending || []).some((p) => p.id === d.id)),
        [docs, pending]
    );
    // §15: employees who accepted older versions get the "new documents" wording.
    const subtitle = (pending || []).length > 0 && alreadyAcked.length > 0
        ? NEW_DOCS_SUBTITLE : GATE_SUBTITLE;

    const allChecked = (pending || []).length > 0 && (pending || []).every((d) => checked[d.id]);

    const submit = async () => {
        if (!allChecked || saving) return;
        setSaving(true);
        setError('');
        try {
            const r = await api.acknowledgeLegal({
                version_ids: (pending || []).map((d) => d.id),
                platform: 'web',
                app_version: APP_VERSION,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            const payload = r?.data || {};
            // The backend re-checks after storing: enter only when it says
            // nothing is pending anymore (never trust local state alone).
            if (payload?.requires_acknowledgement) {
                applyStatus(payload);
                setError('Some documents still require acknowledgement. Please try again.');
                return;
            }
            applyStatus({ pending: [], documents: payload?.documents || docs });
            setAccepted(true);
            setTimeout(() => setAccepted(false), 1500);
        } catch (e) {
            // §12: never enter on failure, keep the ticked boxes, say exactly why.
            setError(e?.response?.data?.error || SUBMIT_ERROR_MESSAGE);
        } finally {
            setSaving(false);
        }
    };

    const renderCard = (doc, { checkbox }) => (
        <Paper key={doc.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                {/* §3/§5: the document name itself is clickable and opens the
                    published text (name, version, effective date, content). */}
                <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    component="button"
                    onClick={() => setViewing(doc)}
                    sx={{
                        border: 'none', background: 'none', p: 0, m: 0, font: 'inherit',
                        color: 'inherit', textAlign: 'left', cursor: 'pointer',
                        textDecoration: 'underline', textDecorationColor: 'primary.main',
                    }}
                >
                    {DOC_LABEL[doc.doc_type] || doc.title} (v{doc.version})
                </Typography>
                {doc.acknowledged ? (
                    <Typography variant="caption" color="success.main" fontWeight={700}>Acknowledged</Typography>
                ) : null}
            </Stack>
            {!!doc.effective_date && (
                <Typography variant="caption" color="text.secondary">Effective {doc.effective_date}</Typography>
            )}
            <Button size="small" onClick={() => setViewing(doc)}>
                View {DOC_LABEL[doc.doc_type] || doc.title}
            </Button>
            {checkbox ? (
                <FormControlLabel
                    sx={{ display: 'block', mt: 1 }}
                    control={
                        <Checkbox
                            checked={!!checked[doc.id]}
                            onChange={(e) => setChecked((prev) => ({ ...prev, [doc.id]: e.target.checked }))}
                            inputProps={{
                                'aria-label': CHECK_TEXT[doc.doc_type] ||
                                    `I acknowledge ${DOC_LABEL[doc.doc_type] || doc.title}.`,
                            }}
                        />
                    }
                    label={CHECK_TEXT[doc.doc_type] || `I acknowledge ${DOC_LABEL[doc.doc_type] || doc.title}.`}
                />
            ) : null}
        </Paper>
    );

    // ── fail-closed loading / error (the app area never renders in these states) ──
    if (pending === null) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 10, p: 3 }}>
                {error ? (
                    <Alert severity="error" action={
                        <Button color="inherit" size="small" onClick={refresh}>Retry</Button>
                    }>{error}</Alert>
                ) : (
                    <>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">Checking policy status…</Typography>
                    </>
                )}
            </Box>
        );
    }

    if (pending.length === 0 && !accepted) return children;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mt: 4 }}>
            <Paper sx={{ maxWidth: 560, width: '100%', p: 3 }}>
                <Typography variant="h6" fontWeight={700}>{GATE_TITLE}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {subtitle}
                </Typography>

                {accepted ? (
                    <Alert severity="success" sx={{ mb: 2 }}>{SUCCESS_MESSAGE}</Alert>
                ) : null}

                <Stack spacing={1.5}>
                    {(pending || []).map((d) => renderCard(d, { checkbox: true }))}
                    {optionalDocs.map((d) => renderCard(d, { checkbox: false }))}
                    {alreadyAcked.map((d) => renderCard(d, { checkbox: false }))}
                </Stack>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                {(pending || []).length > 0 ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            disabled={!allChecked || saving}
                            onClick={submit}
                            aria-label={ACCEPT_LABEL}
                        >
                            {saving ? <CircularProgress size={20} color="inherit" /> : ACCEPT_LABEL}
                        </Button>
                        <Button onClick={logout}>Log out</Button>
                    </Stack>
                ) : null}
            </Paper>
            <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle>{viewing?.title}</DialogTitle>
                <DialogContent dividers>{viewing && <DocumentBody doc={viewing} />}</DialogContent>
                <DialogActions><Button onClick={() => setViewing(null)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default LegalGate;
